import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Card,
  CardContent,
  CardActions,
  Grid,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ILoyaltyPrograms } from '../utils/types';
import { deleteLoyaltyPrograms, loyaltyPrograms } from '../api';
import { AuthContext } from '../context/AuthContext';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import Add from '@mui/icons-material/AddCircleOutline';
import AddEditLoyaltyProgramDialog from '../components/AddEditLoyaltyProgramsDialog';
import ConfirmDialog from '../components/ConfirmDialog';

const AccountOtherInformationContainer: React.FC = () => {
  const { t } = useTranslation();
  const { token } = useContext(AuthContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const [error, setError] = useState<string | undefined>();
  const [loyaltyProgramResults, setLoyaltyProgramResults] = useState<
    ILoyaltyPrograms[]
  >([]);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [selectedProgram, setSelectedProgram] = useState<
    ILoyaltyPrograms | undefined
  >();
  const [confirmDialogOpen, setConfirmDialogOpen] = useState<boolean>(false);
  const [programToDelete, setProgramToDelete] = useState<number | null>(null);

  useEffect(() => {
    fetchLoyaltyPrograms();
  }, []);

  const fetchLoyaltyPrograms = async () => {
    try {
      const res = await loyaltyPrograms(token);
      const data = await res.json();

      if (res?.status === 200) {
        setLoyaltyProgramResults(data);
      } else {
        throw data;
      }
    } catch (e: any) {
      setError(e.detail || 'Error');
      console.log(error, e);
    }
  };

  const deleteLoyaltyProgramItem = async (id: number) => {
    const res = await fetchDeleteLoyaltyProgram(id);
    if (res?.status === 200) {
      setLoyaltyProgramResults((prev) =>
        prev.filter((program) => program.id !== id),
      );
    } else {
      console.error('Failed to delete item:', res);
    }
  };

  const editLoyaltyProgramItem = async (program?: ILoyaltyPrograms) => {
    setSelectedProgram(program);
    setOpenDialog(true);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setSelectedProgram(undefined);
  };

  const handleSaveLoyaltyProgram = async () => {
    setOpenDialog(false);
    await fetchLoyaltyPrograms();
  };

  const fetchDeleteLoyaltyProgram = async (id: number) => {
    try {
      const res = await deleteLoyaltyPrograms(id, token);
      return res;
    } catch (e: any) {
      setError(e.detail);
      console.error('Delete Loyalty Program Error:', e);
      console.log(error);
    }
  };

  const handleDeleteClick = (id: number) => {
    setProgramToDelete(id);
    setConfirmDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (programToDelete !== null) {
      await deleteLoyaltyProgramItem(programToDelete);
      setConfirmDialogOpen(false);
      setProgramToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setConfirmDialogOpen(false);
    setProgramToDelete(null);
  };

  return (
    <Box sx={{ maxWidth: 480, mt: 4 }}>
      <Button
        color="primary"
        sx={{
          display: 'flex',
          marginLeft: 'auto',
          marginTop: 2,
          marginBottom: 1,
        }}
        onClick={() => editLoyaltyProgramItem()}
      >
        <Add />
      </Button>

      {loyaltyProgramResults.length === 0 ? (
        <Typography variant="h6" align="center">
          {t('accountInfosPage.noLoyaltyPrograms')}{' '}
        </Typography>
      ) : (
        <Grid container spacing={2}>
          {loyaltyProgramResults.map((program) => (
            <Grid item xs={36} sm={24} md={16} key={program.id}>
              <Card>
                <CardContent>
                  <Typography variant="h6">
                    {program?.company?.name || t('accountInfosPage.unknown')}
                  </Typography>
                  <Typography variant="body2">
                    {t('accountInfosPage.membershipNumber')}:{' '}
                    {program?.membershipNumber ||
                      t('accountInfosPage.notAvailable')}
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button onClick={() => editLoyaltyProgramItem(program)}>
                    <EditIcon fontSize="medium" color="action" />
                  </Button>
                  <Button onClick={() => handleDeleteClick(program.id)}>
                    <DeleteIcon fontSize="medium" color="error" />
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <AddEditLoyaltyProgramDialog
        open={openDialog}
        onClose={handleDialogClose}
        program={selectedProgram}
        onSave={handleSaveLoyaltyProgram}
      />

      <ConfirmDialog
        open={confirmDialogOpen}
        title={t('accountInfosPage.deleteLoyaltyProgram')}
        description={t('accountInfosPage.confirmDeletion')}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </Box>
  );
};

export default AccountOtherInformationContainer;
